// À propos — page publique (indexable).

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "../SEOHead";
import { seoConfig, jsonLdBreadcrumb } from "../seoConfig";

const P = seoConfig.pages.aPropos;

const APropos = () => {
  return (
    <>
      <SEOHead
        title={P.title}
        description={P.description}
        keywords={P.keywords}
        path={P.path}
        jsonLd={jsonLdBreadcrumb([
          { name: "Accueil", path: "/" },
          { name: "À propos", path: P.path },
        ])}
      />

      <section className="mx-auto max-w-3xl px-6 lg:px-10 pt-20 pb-16 lg:pt-28">
        <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
          À propos
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#111]">
          Une plateforme pensée<br />depuis le terrain.
        </h1>
        <p className="mt-6 text-[15px] lg:text-[17px] leading-[1.65] text-[#6B7280]">
          WYVIO est née d&apos;un constat simple : il existe des outils puissants pour gérer un stock,
          une comptabilité ou une relation commerciale, mais très peu pour piloter, simplement,
          la relation client au quotidien. Trop d&apos;informations restent dans des tableaux Excel,
          des SMS, des conversations téléphoniques ou des mémoires fragiles.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-black/5 p-8 bg-white">
            <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
              Ce que nous croyons
            </p>
            <p className="mt-4 text-[14px] text-[#111] leading-relaxed">
              La transparence n'est pas un supplément marketing. C'est ce qui évite
              les malentendus, les rappels inutiles et les litiges. WYVIO met la même
              information à disposition de l&apos;équipe et du client, au moment où elle
              devient utile.
            </p>
          </div>
          <div className="rounded-2xl border border-black/5 p-8 bg-white">
            <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
              Notre approche
            </p>
            <p className="mt-4 text-[14px] text-[#111] leading-relaxed">
              Une seule chose bien faite plutôt que dix choses approximatives. WYVIO
              couvre le suivi opérationnel de votre relation client — rien de plus.
              Les fonctionnalités qui ne servent pas ce périmètre n'existent
              délibérément pas.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-10 py-16">
        <h2 className="text-2xl font-semibold tracking-[-0.02em] text-[#111]">
          Ce qui nous guide au quotidien
        </h2>
        <ul className="mt-8 space-y-5 text-[15px] text-[#111] leading-relaxed">
          <li className="flex gap-4">
            <span className="text-[#8FB600] font-semibold w-8 shrink-0">01</span>
            <span>
              <strong className="text-[#111]">Le moins d'oublis possible.</strong>{" "}
              Un dossier ne doit jamais dépendre de la vigilance d'une seule personne.
            </span>
          </li>
          <li className="flex gap-4">
            <span className="text-[#8FB600] font-semibold w-8 shrink-0">02</span>
            <span>
              <strong className="text-[#111]">Une seule source de vérité.</strong>{" "}
              L&apos;équipe et le client voient la même chose, chacun avec le niveau de détail approprié.
            </span>
          </li>
          <li className="flex gap-4">
            <span className="text-[#8FB600] font-semibold w-8 shrink-0">03</span>
            <span>
              <strong className="text-[#111]">Aucune suppression, jamais.</strong>{" "}
              Tout ce qui a été échangé reste consultable. Les archives protègent
              l&apos;équipe comme le client.
            </span>
          </li>
          <li className="flex gap-4">
            <span className="text-[#8FB600] font-semibold w-8 shrink-0">04</span>
            <span>
              <strong className="text-[#111]">Un minimum de clics pour l&apos;équipe.</strong>{" "}
              Chaque interaction est pensée pour être exécutée en une seule action.
            </span>
          </li>
        </ul>
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-10 py-16">
        <div className="rounded-2xl bg-[#111] text-white p-10">
          <h2 className="text-2xl font-semibold tracking-[-0.02em]">Envie d'en discuter&nbsp;?</h2>
          <p className="mt-3 text-[14.5px] text-white/70 leading-relaxed">
            Nous adorons échanger avec les professionnels du secteur, même en dehors
            de tout projet immédiat.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="mt-6 h-11 inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] text-[#111] text-[13.5px] font-semibold px-5 hover:brightness-95 transition"
          >
            Nous écrire
            <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default APropos;
